import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useCallback } from 'react';
import useSWR, { type SWRConfiguration } from 'swr';
import { type ApiClient, useApi } from '../../providers/api';

const graphScopes = ['https://graph.microsoft.com/User.ReadBasic.All'];

const isSandbox = import.meta.env.MODE === 'sandbox';

const graphSWRConfig: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateIfStale: false,
  shouldRetryOnError: false,
};

export type GraphUser = {
  id: string;
  displayName: string;
};

const userSelectFields = ['id', 'displayName'].join(',');

const useGraphToken = () => {
  const { accounts, instance } = useMsal();

  return useCallback(async (): Promise<string | null> => {
    const request = { account: accounts[0], scopes: graphScopes };
    try {
      const result = await instance.acquireTokenSilent(request);
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        return null;
      }
      throw err;
    }
  }, [accounts, instance]);
};

// Fallback: hit our API which uses app-access. Used when the delegated MS Graph
// call returns 401/403/404 (e.g. B2B guests who can't read our org's users).
const fetchGraphUserViaRoute = async (
  client: ApiClient,
  oid: string
): Promise<GraphUser | null> => {
  const response = await client.v1.graph.users.$post({ json: { oids: oid } });
  if (!response.ok) {
    throw new Error(`Graph application access API error: ${response.status}`);
  }
  const data = (await response.json()) as { items: GraphUser[]; notFoundOids: string[] };
  return data.items[0] ?? null;
};

export function useGraphUser(oid: string | null | undefined) {
  const getToken = useGraphToken();
  const { client } = useApi();

  const fetcher = useCallback(async (): Promise<GraphUser | null> => {
    const accessToken = await getToken();
    if (!accessToken) {
      return fetchGraphUserViaRoute(client, oid!);
    }
    const response = await fetch(
      `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(oid!)}?$select=${userSelectFields}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (response.status === 401 || response.status === 403 || response.status === 404) {
      return fetchGraphUserViaRoute(client, oid!);
    }
    if (!response.ok) {
      throw new Error(`Graph API error: ${response.status}`);
    }

    return (await response.json()) as GraphUser;
  }, [oid, getToken, client]);

  return useSWR<GraphUser | null>(
    oid && !isSandbox ? `graph/users/${oid}` : null,
    fetcher,
    graphSWRConfig
  );
}
